import { FC, useEffect, useMemo, useState } from "react";
import TrashSimple from "@components/Icons/TrashSimple";
import PencilSimple from "@components/Icons/PencilSimple";
import PlusCircle from "@components/Icons/PlusCircle";
import Expression from '@/helpers/expressions/ThisExpression';

import Select, { Option } from '@/components/DesignLibrary/Atoms/Select';

import makeDefaultExpression from '@/helpers/makeDefaultExpression';
import s from "./ExpressionEditor.module.scss";
import Buttons from "../Buttons";
import {
  buildExpressionAst,
  Comparison,
  ConditionsLabel,
  generateExpressionString,
  parseConditions,
} from "./utils";
import Modal from "../Modal";
import { Input } from "../DesignLibrary/Atoms";
import Button from "../DesignLibrary/Button";

type FieldInfo = {
  key: string;
  label: string;
  type: string;
  selector?: string;
  options?: { key: string; label: string }[];
};

type SingleCondition = {
  field: string;
  operator: Comparison;
  value?: string;
  selector?: string;
  logicalOperator?: '&&' | '||';
};

const ConditionOptions: Record<string, Option> = {};

Object.entries(ConditionsLabel).forEach(([key, label]) => {
  ConditionOptions[key] = { key, label };
});

const SimpleEditor: FC<{
  expression: Pick<Expression, 'expression'> | null;
  onChange?: (expression: Expression) => void;
  fieldsInfo: Record<string, FieldInfo>;
}> = ({
  expression,
  onChange,
  fieldsInfo,
}) => {
  const [conditions, setConditions] = useState<SingleCondition[]>([]);

  const [editingConditionIndex, setEditingConditionIndex] = useState<number | null>(null);
  const [showConditionEditor, setShowConditionEditor] = useState(false);

  const [newCondition, setNewCondition] = useState<SingleCondition>({
    field: '',
    logicalOperator: undefined,
    operator: Comparison.Equal,
    value: '',
  });

  const getAvailableConditions = (fieldKey: string): Option[] => {
    const fieldType = fieldsInfo[fieldKey]?.type;

    switch (fieldType) {
    case 'short-text':
    case 'long-text':
    case 'name':
    case 'address-international':
    case 'address-us':
    case 'ssn':
    case 'phone-number':
      return [
        ConditionOptions[Comparison.Equal],
        ConditionOptions[Comparison.NotEqual],
        ConditionOptions[Comparison.Filled],
        ConditionOptions[Comparison.NotFilled],
      ];
    case 'number':
    case 'numeric':
    case 'currency':
    case 'percent':
    case 'date':
      return [
        ConditionOptions[Comparison.Equal],
        ConditionOptions[Comparison.NotEqual],
        ConditionOptions[Comparison.GreaterThan],
        ConditionOptions[Comparison.LessThan],
        ConditionOptions[Comparison.Filled],
        ConditionOptions[Comparison.NotFilled],
      ];
    case 'single-select':
    case 'selection':
      return [
        ConditionOptions[Comparison.Equal],
        ConditionOptions[Comparison.NotEqual],
        ConditionOptions[Comparison.Filled],
        ConditionOptions[Comparison.NotFilled],
      ];
    case 'multi-select':
      return [
        ConditionOptions[Comparison.Equal],
        ConditionOptions[Comparison.NotEqual],
        ConditionOptions[Comparison.Filled],
        ConditionOptions[Comparison.NotFilled],
        ConditionOptions[Comparison.Includes],
      ];
    default:
      return [
        ConditionOptions[Comparison.Equal],
        ConditionOptions[Comparison.NotEqual],
      ];
    }
  };

  const fieldOptions: Option[] = useMemo(
    () =>
      Object.values(fieldsInfo).map(field => ({
        key: field.key,
        label: field.label,
        selector: field.selector,
        type: field.type,
      })),
    [fieldsInfo],
  );

  const addNewCondition = (): void => {
    setEditingConditionIndex(conditions.length);
    setNewCondition({
      field: '',
      logicalOperator: conditions.length > 0 ? '&&' : undefined,
      operator: Comparison.Equal,
      value: '',
    });
    setShowConditionEditor(true);
  };

  const saveCombinedExpression = (conds: SingleCondition[]): void => {
    if (conds.length === 0) {
      onChange?.(makeDefaultExpression());
      return;
    }

    const ast = buildExpressionAst(conds);
    if (ast) {
      const expressionString = generateExpressionString(ast);
      const variableMap = new Map<string, {
        referenceId: string;
        referenceType: Expression['variables'][number]['referenceType'];
        selector: string;
        variableName: string;
      }>();

      conds.forEach(cond => {
        const variableName = cond.field;
        const referenceId = cond.field.includes('context') ? 'self' : cond.field;
        const referenceType = cond.field.includes('context') ? 'workflow-context' : 'field-template-key';
        const selector = cond.selector || '';

        if (!variableMap.has(variableName)) {
          variableMap.set(variableName, {
            referenceId,
            referenceType,
            selector,
            variableName,
          });
        } else {
          const existingVariable = variableMap.get(variableName);
          if (existingVariable) {
            if (!existingVariable.selector && selector) {
              existingVariable.selector = selector;
            } else if (existingVariable.selector !== selector && selector) {
              existingVariable.selector = selector;
            }
            variableMap.set(variableName, existingVariable);
          }
        }
      });

      const variables = Array.from(variableMap.values());

      onChange?.({
        expression: expressionString,
        filterExpression: null,
        filterExpressionItemSelector: null,
        filterExpressionOperand: null,
        filterExpressionReducer: null,
        filterExpressionVariableName: null,
        variables,
      });
    }
  };

  const deleteCondition = (index: number): void => {
    if (window.confirm('Are you sure you want to delete this condition?')) {
      const updatedConditions = conditions.filter((_, idx) => idx !== index);
      if (updatedConditions.length > 0) {
        updatedConditions[0].logicalOperator = undefined;
      }
      setConditions(updatedConditions);
      saveCombinedExpression(updatedConditions);
    }
  };

  const editCondition = (index: number): void => {
    setEditingConditionIndex(index);
    setNewCondition(conditions[index]);
    setShowConditionEditor(true);
  };

  const saveCondition = (): void => {
    const updatedConditions = [...conditions];
    if (editingConditionIndex !== null && editingConditionIndex < conditions.length) {
      updatedConditions[editingConditionIndex] = newCondition;
    } else {
      updatedConditions.push(newCondition);
    }
    setConditions(updatedConditions);
    setShowConditionEditor(false);
    setNewCondition({
      field: '',
      logicalOperator: undefined,
      operator: Comparison.Equal,
      value: '',
    });
    setEditingConditionIndex(null);
    saveCombinedExpression(updatedConditions);
  };

  useEffect(() => {
    if (expression?.expression) {
      setConditions(parseConditions(expression.expression));
    } else {
      setConditions([]);
    }
  }, [expression]);

  return (
    <>
      <table className={s.conditions}>
        <tbody>
          {conditions.map((condition, idx) => (
            <tr key={idx}>
              <td className={idx === 0 ? s.if : s.andOr}>
                <div>
                  {(() => {
                    if (idx === 0) return 'If';
                    if (condition.logicalOperator === '&&') return 'And';
                    return 'Or';
                  })()}
                </div>
              </td>
              <td title={fieldsInfo[condition.field]?.label || condition.field}>{fieldsInfo[condition.field]?.label || condition.field}</td>
              <td className={s.condition} title={ConditionsLabel[condition.operator]}>
                {ConditionsLabel[condition.operator]}
              </td>
              {![Comparison.Filled, Comparison.NotFilled].includes(condition.operator)
                ? (
                  <td title={condition.value}>{condition.value || 'NULL'}</td>
                )
                : (
                  <td />
                )}
              <td className={s.edit} title="Edit">
                <Buttons isWrapper onClick={() => editCondition(idx)}>
                  <PencilSimple weight="fill" color="var(--color-gray-75)" size={12} />
                </Buttons>
              </td>
              <td className={s.edit} title="Delete">
                <Buttons isWrapper onClick={() => deleteCondition(idx)}>
                  <TrashSimple weight="fill" color="var(--color-error)" size={12} />
                </Buttons>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={s.buttons}>
        <Button variant="secondary" size="sm" onClick={addNewCondition}>
          <PlusCircle weight="fill" color="var(--color-noodle)" size={12} style={{ marginRight: 4 }} /> Add Condition
        </Button>
      </div>
      {showConditionEditor && (
        <Modal hasUnsavedChanges title="Condition Editor" onClose={() => setShowConditionEditor(false)}>
          <div className={s.conditionEditor}>
            <>
              {editingConditionIndex !== null && editingConditionIndex > 0 && (
                <Select
                  label="Logical Operator"
                  options={[
                    { key: '&&', label: 'And' },
                    { key: '||', label: 'Or' },
                  ]}
                  value={newCondition.logicalOperator || '&&'}
                  onChange={value =>
                    setNewCondition({
                      ...newCondition,
                      logicalOperator: value as '&&' | '||',
                    })
                  }
                  optional
                />
              )}
              <Select
                label="Field"
                options={fieldOptions}
                value={newCondition.field}
                onChange={value => setNewCondition({ ...newCondition, field: value, selector: fieldsInfo[value]?.selector || ''})}
                optional
              />
              {newCondition.field && (
                <>
                  <Select
                    label="Condition"
                    options={getAvailableConditions(newCondition.field)}
                    value={newCondition.operator}
                    onChange={value =>
                      setNewCondition({
                        ...newCondition,
                        operator: value as Comparison,
                      })
                    }
                    optional
                  />
                  {![Comparison.Filled, Comparison.NotFilled].includes(newCondition.operator) && (
                    <>
                      {['multi-select', 'single-select', 'selection'].includes(fieldsInfo[newCondition.field]?.type)
                        ? (
                          <Select
                            label="Value"
                            options={
                              fieldsInfo[newCondition.field]?.options?.map(({ key, label }) => ({
                                key,
                                label,
                              })) ?? []
                            }
                            value={newCondition.value}
                            onChange={value => setNewCondition({ ...newCondition, value })}
                            optional
                          />
                        )
                        : (
                          <Input label="Value" value={newCondition.value} onChange={value => setNewCondition({ ...newCondition, value })} optional />
                        )}
                    </>
                  )}
                  <Button variant="primary" size="md" onClick={saveCondition}>
                    Done
                  </Button>
                </>
              )}
            </>
          </div>
        </Modal>
      )}
    </>
  );
};

export default SimpleEditor;
